import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useTranslation } from '@/lib/i18n';
import {
  Button, PageShell, Skeleton, Pagination, DataTable, StatusBadge, RowActions, SectionCard, Input,
  FilterBar, FilterField, RadixSelect, toast, toggleDataTableSort,
  type DataTableColumn, type DataTableSort, type RowActionItem, type StatusBadgeProps,
} from '@/components/ui';
import { ArrowLeft, ScanLine, Download, CalendarDays, List as ListIcon } from 'lucide-react';
import { friendlyMessage } from '@/lib/errors';
import {
  downloadPopupReservationsExport,
  QrVerifyError,
  usePopupQrVerifyMutation,
  usePopupReservationActions,
  usePopupReservationsAdminQuery,
  usePopupSlotsQuery,
} from '../popups/hooks/usePopupConfig';
import { usePopupQuery } from '../hooks/usePopupsQuery';
import { QrCameraScanner } from '../popups/components/QrCameraScanner';
import type {
  PopupReservationAdminFilter,
  PopupReservationAdminView,
  PopupReservationStatus,
  PopupSlotView,
} from '../popups/types/popupDetail';

const PAGE_SIZE = 20;

const STATUS_VARIANT: Record<PopupReservationStatus, StatusBadgeProps['variant']> = {
  RESERVED: 'info',
  BOOKED: 'info',
  CONFIRMED: 'progress',
  ENTERED: 'success',
  // QA-184: '이용 완료' = 정상 종료(성공 톤).
  USED: 'success',
  CANCELLED: 'neutral',
  NOSHOW: 'warning',
  REFUNDED: 'danger',
};

// QA-184: 필터 셀렉트에 노출할 상태(운영 의미 있는 상태). raw enum 직접 노출 대신 라벨화.
const FILTER_STATUSES: ReadonlyArray<PopupReservationStatus> = [
  'CONFIRMED',
  'ENTERED',
  'USED',
  'NOSHOW',
  'CANCELLED',
  'REFUNDED',
];

const formatDateTime = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('ko-KR') : '-';

type ViewMode = 'list' | 'calendar';

export const PopupReservationsPage = () => {
  const { t } = useTranslation('common');
  const { popupId } = useParams<{ popupId: string }>();
  const id = popupId ?? '';
  const [page, setPage] = useState(1);
  const [view, setView] = useState<ViewMode>('list');

  // QA-184: 검색/필터 상태(상태/키워드/이용일 범위). 빈 값 = 전체.
  const [statusFilter, setStatusFilter] = useState<PopupReservationStatus | ''>('');
  const [keyword, setKeyword] = useState('');
  const [usedFrom, setUsedFrom] = useState('');
  const [usedTo, setUsedTo] = useState('');
  // 입력 즉시가 아니라 검색 버튼/엔터로 적용되는 확정 필터(목록 재조회 트리거).
  const [appliedFilter, setAppliedFilter] = useState<PopupReservationAdminFilter>({});
  const [exporting, setExporting] = useState(false);
  // QA-184(서버 정렬): 컬럼 헤더 클릭 정렬을 서버(Pageable.sort)로 전달 → 전체 결과 기준 정렬.
  // null = 기본(createdAt,desc). 엔티티 비매핑 컬럼(popupName)은 서버가 기본 정렬하므로
  // 아래 items useMemo 가 현재 페이지를 클라이언트에서 추가 정렬하는 폴백으로 남겨 둔다.
  const [sort, setSort] = useState<DataTableSort | null>(null);

  const popupQuery = usePopupQuery(id);
  const popupName = popupQuery.data?.name ?? '';

  // QA-184(서버 정렬): 컬럼 헤더 정렬을 서버(Pageable.sort)로 전달 — 전체 결과 기준 정렬 후 페이지네이션.
  const reservationsQuery = usePopupReservationsAdminQuery(id, page, PAGE_SIZE, appliedFilter, sort);
  const { regenerateQr, refund, noShow, markUsed } = usePopupReservationActions(id);

  const rawItems = reservationsQuery.data?.items ?? [];
  const pagination = reservationsQuery.data?.pagination;

  const statusLabel = (row: PopupReservationAdminView): string =>
    // QA-184: BE 공급 라벨 우선(raw 금지), 미공급 시 i18n 폴백.
    row.statusLabel ??
    t(`cms.popups.reservationStatus.${row.status}`, { defaultValue: row.status });

  // QA-R6 J: 표시용 정렬값 추출기 — 컬럼 key 별 비교 키(문자열/숫자). 시간/일자는 ISO·문자열
  // 비교로 안정적이고, 누락값은 일관되게 뒤로 가도록 빈 문자열/0 처리한다.
  // ★statusLabel 을 위에서 먼저 선언해야 한다 — 아래 items useMemo 가 렌더 시점에 sortValue
  //   → statusLabel 을 호출하므로, statusLabel 이 const(비호이스팅)면 TDZ ReferenceError 가 난다.
  const sortValue = (row: PopupReservationAdminView, key: string): string | number => {
    switch (key) {
      case 'reservationNo':
        return row.reservationNo ?? '';
      case 'reserver':
        return (row.reserverName ?? '').toLowerCase();
      case 'popupName':
        return (row.popupName ?? popupName ?? '').toLowerCase();
      case 'slot':
        return `${row.slotDate ?? ''} ${row.slotTime ?? ''}`;
      case 'peopleCount':
        return row.peopleCount ?? 0;
      case 'status':
        return statusLabel(row).toLowerCase();
      case 'enteredAt':
        return row.enteredAt ?? '';
      default:
        return '';
    }
  };

  const items = useMemo(() => {
    if (!sort) return rawItems;
    const dir = sort.direction === 'asc' ? 1 : -1;
    // 원본 불변 — 복사본을 정렬한다(React Query 캐시 배열 직접 변형 금지).
    return [...rawItems].sort((a, b) => {
      const av = sortValue(a, sort.key);
      const bv = sortValue(b, sort.key);
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av).localeCompare(String(bv), 'ko-KR') * dir;
    });
    // sortValue 는 popupName/statusLabel(t) 의존 — 안정적이라 deps 에 sort/rawItems 만.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawItems, sort, popupName]);

  const applyFilters = () => {
    setPage(1);
    setAppliedFilter({
      ...(statusFilter ? { status: statusFilter } : {}),
      ...(keyword.trim() ? { keyword: keyword.trim() } : {}),
      ...(usedFrom ? { usedFrom } : {}),
      ...(usedTo ? { usedTo } : {}),
    });
  };

  const resetFilters = () => {
    setStatusFilter('');
    setKeyword('');
    setUsedFrom('');
    setUsedTo('');
    setPage(1);
    setAppliedFilter({});
  };

  const onExport = async () => {
    setExporting(true);
    try {
      await downloadPopupReservationsExport(id, appliedFilter);
    } catch (e) {
      toast.error(
        friendlyMessage(e, t('cms.popups.reservationExport.failed', { defaultValue: '예약자 내려받기에 실패했습니다.' })),
      );
    } finally {
      setExporting(false);
    }
  };

  const columns = useMemo<ReadonlyArray<DataTableColumn<PopupReservationAdminView>>>(
    () => [
      {
        key: 'reservationNo',
        header: t('cms.popups.reservationCols.reservationNo', { defaultValue: '예약번호' }),
        width: 120,
        sortable: true,
        cell: (row) => (
          <span className="font-mono text-xs" style={{ color: 'var(--content-muted)' }}>
            {/* 내부 rowId(reservationId) 가 아니라 BE 공급 비식별 예약번호만 노출. */}
            {row.reservationNo ?? '-'}
          </span>
        ),
      },
      {
        key: 'reserver',
        header: t('cms.popups.reservationCols.reserver'),
        sortable: true,
        cell: (row) => {
          const name = row.reserverName ?? '-';
          const contact = [row.reserverPhone, row.reserverEmail].filter(Boolean).join(' · ');
          return (
            <div className="flex min-w-0 flex-col gap-0.5">
              <span className="truncate text-sm font-medium" style={{ color: 'var(--content-strong)' }} title={name}>
                {name}
              </span>
              {contact ? (
                <span className="truncate text-xs" style={{ color: 'var(--content-muted)' }} title={contact}>
                  {contact}
                </span>
              ) : null}
            </div>
          );
        },
      },
      {
        key: 'popupName',
        header: t('cms.popups.reservationCols.project', { defaultValue: '프로젝트명' }),
        width: 160,
        sortable: true,
        cell: (row) => (
          <span className="truncate text-sm" style={{ color: 'var(--content-default)' }} title={row.popupName ?? popupName}>
            {row.popupName ?? popupName ?? '-'}
          </span>
        ),
      },
      {
        key: 'slot',
        header: t('cms.popups.reservationCols.slot'),
        width: 168,
        sortable: true,
        cell: (row) => (
          <span className="text-sm tabular-nums" style={{ color: 'var(--content-default)' }}>
            {row.slotDate} {row.slotTime}
          </span>
        ),
      },
      {
        key: 'peopleCount',
        header: t('cms.popups.reservationCols.peopleCount'),
        align: 'right',
        width: 64,
        sortable: true,
        cell: (row) => (
          <span className="text-sm tabular-nums" style={{ color: 'var(--content-muted)' }}>
            {row.peopleCount}
          </span>
        ),
      },
      {
        key: 'status',
        header: t('cms.popups.reservationCols.status'),
        align: 'center',
        width: 96,
        sortable: true,
        cell: (row) => <StatusBadge variant={STATUS_VARIANT[row.status]} label={statusLabel(row)} />,
      },
      {
        key: 'enteredAt',
        header: t('cms.popups.reservationCols.entered'),
        width: 168,
        sortable: true,
        cell: (row) => (
          <span className="text-xs tabular-nums" style={{ color: 'var(--content-muted)' }}>
            {formatDateTime(row.enteredAt)}
          </span>
        ),
      },
      {
        key: 'actions',
        header: '',
        align: 'right',
        width: 56,
        cell: (row) => {
          // QA-184: '이용 완료' 처리는 입장/확정 등 진행 상태에서만 의미(이미 완료/취소 제외).
          const canMarkUsed =
            row.status === 'ENTERED' ||
            row.status === 'CONFIRMED' ||
            row.status === 'BOOKED' ||
            row.status === 'RESERVED';
          const actions: RowActionItem[] = [
            ...(canMarkUsed
              ? [
                  {
                    key: 'use',
                    label: t('cms.popups.reservationActions.markUsed', { defaultValue: '이용 완료 처리' }),
                    disabled: markUsed.isPending,
                    onClick: () => markUsed.mutate(row.reservationId),
                  } satisfies RowActionItem,
                ]
              : []),
            {
              key: 'qr',
              label: t('cms.popups.reservationActions.regenerateQr'),
              disabled: regenerateQr.isPending,
              onClick: () => regenerateQr.mutate(row.reservationId),
            },
            {
              key: 'refund',
              label: t('cms.popups.reservationActions.refund'),
              disabled: refund.isPending,
              onClick: () => refund.mutate(row.reservationId),
            },
            {
              key: 'noshow',
              label: t('cms.popups.reservationActions.noShow'),
              variant: 'danger',
              separatorBefore: true,
              disabled: noShow.isPending,
              onClick: () => noShow.mutate(row.reservationId),
            },
          ];
          return <RowActions triggerLabel={t('cms.popups.reservationActions.menu')} actions={actions} />;
        },
      },
    ],
    [t, popupName, regenerateQr, refund, noShow, markUsed],
  );

  return (
    <PageShell
      title={
        <span className="flex flex-col gap-0.5">
          <span>{t('cms.popups.reservationsTitle')}</span>
          {popupName ? (
            <span className="text-xs font-normal" style={{ color: 'var(--content-muted)' }}>
              {popupName}
            </span>
          ) : null}
        </span>
      }
      actions={
        // QA-R6 J: [뒤로] 동선 — 예약현황에서 나갈 때 해당 팝업의 정보 수정 페이지로 복귀해야 한다
        // (재신고: 직전 라운드가 목록으로 보냈으나 운영자는 그 팝업 편집으로 돌아가려는 흐름).
        // popupId 가 있으면 그 팝업 편집(cms/popups/:popupId)으로, 없으면(드문 경로) 목록 폴백.
        <Link to={id ? `/cms/popups/${id}` : '/cms/popups'}>
          <Button variant="ghost" size="sm">
            <ArrowLeft size={14} className="mr-1" aria-hidden="true" />
            {t('cms.popups.backToEdit', { defaultValue: '팝업 정보 수정' })}
          </Button>
        </Link>
      }
      data-testid="popup-reservations-page"
    >
      <QrVerifyPanel popupId={id} />

      {/* QA-184: 목록 / 캘린더 뷰 토글. */}
      <div className="flex items-center gap-1" role="tablist" aria-label={t('cms.popups.viewToggle', { defaultValue: '보기 전환' })}>
        <Button
          variant={view === 'list' ? 'primary' : 'ghost'}
          size="sm"
          role="tab"
          aria-selected={view === 'list'}
          onClick={() => setView('list')}
        >
          <ListIcon size={14} className="mr-1" aria-hidden="true" />
          {t('cms.popups.viewList', { defaultValue: '목록' })}
        </Button>
        <Button
          variant={view === 'calendar' ? 'primary' : 'ghost'}
          size="sm"
          role="tab"
          aria-selected={view === 'calendar'}
          onClick={() => setView('calendar')}
        >
          <CalendarDays size={14} className="mr-1" aria-hidden="true" />
          {t('cms.popups.viewCalendar', { defaultValue: '캘린더' })}
        </Button>
      </div>

      {view === 'calendar' ? (
        <ReservationCalendar popupId={id} />
      ) : (
        <SectionCard title={t('cms.popups.reservationsListTitle')}>
          {/* QA-184: 검색/필터 + 내려받기(export). */}
          <FilterBar
            aria-label={t('cms.popups.reservationsListTitle')}
            actions={
              <div className="flex items-center gap-2">
                <Button variant="primary" size="sm" onClick={applyFilters} data-testid="reservation-search">
                  {t('cms.popups.reservationFilter.search', { defaultValue: '검색' })}
                </Button>
                <Button variant="ghost" size="sm" onClick={resetFilters}>
                  {t('cms.popups.reservationFilter.reset', { defaultValue: '초기화' })}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => void onExport()}
                  disabled={exporting}
                  data-testid="reservation-export"
                >
                  <Download size={14} className="mr-1" aria-hidden="true" />
                  {exporting
                    ? t('cms.popups.reservationExport.exporting', { defaultValue: '내려받는 중…' })
                    : t('cms.popups.reservationExport.action', { defaultValue: '내려받기' })}
                </Button>
              </div>
            }
            filters={
              <>
                <FilterField label={t('cms.popups.reservationFilter.status', { defaultValue: '예약상태' })}>
                  <RadixSelect
                    value={statusFilter || 'ALL'}
                    onChange={(v) => setStatusFilter(v === 'ALL' ? '' : (v as PopupReservationStatus))}
                    options={[
                      { value: 'ALL', label: t('cms.popups.reservationFilter.statusAll', { defaultValue: '전체 상태' }) },
                      ...FILTER_STATUSES.map((s) => ({
                        value: s,
                        label: t(`cms.popups.reservationStatus.${s}`, { defaultValue: s }),
                      })),
                    ]}
                    aria-label={t('cms.popups.reservationFilter.status', { defaultValue: '예약상태' })}
                  />
                </FilterField>
                <FilterField label={t('cms.popups.reservationFilter.keyword', { defaultValue: '검색어' })}>
                  <Input
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        applyFilters();
                      }
                    }}
                    placeholder={t('cms.popups.reservationFilter.keywordPlaceholder', {
                      defaultValue: '예약자명·연락처·예약번호',
                    })}
                    aria-label={t('cms.popups.reservationFilter.keyword', { defaultValue: '검색어' })}
                  />
                </FilterField>
                <FilterField label={t('cms.popups.reservationFilter.usedFrom', { defaultValue: '이용일 시작' })}>
                  <Input
                    type="date"
                    value={usedFrom}
                    onChange={(e) => setUsedFrom(e.target.value)}
                    aria-label={t('cms.popups.reservationFilter.usedFrom', { defaultValue: '이용일 시작' })}
                  />
                </FilterField>
                <FilterField label={t('cms.popups.reservationFilter.usedTo', { defaultValue: '이용일 종료' })}>
                  <Input
                    type="date"
                    value={usedTo}
                    onChange={(e) => setUsedTo(e.target.value)}
                    aria-label={t('cms.popups.reservationFilter.usedTo', { defaultValue: '이용일 종료' })}
                  />
                </FilterField>
              </>
            }
          />

          {pagination ? (
            <p className="mb-2 text-xs" style={{ color: 'var(--content-muted)' }}>
              {t('cms.popups.reservationCount', {
                count: pagination.totalItems,
                defaultValue: '예약 {{count}}건',
              })}
            </p>
          ) : null}

          {reservationsQuery.isPending ? (
            <div role="status" aria-live="polite" className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-[44px] w-full" />
              ))}
            </div>
          ) : reservationsQuery.isError ? (
            <div
              role="alert"
              aria-live="polite"
              className="flex flex-col items-center gap-2 rounded-lg py-12 text-center text-sm"
              style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
            >
              <p>{t('cms.popups.loadFailed')}</p>
              <Button variant="outline" size="sm" onClick={() => reservationsQuery.refetch()}>
                {t('cms.retry')}
              </Button>
            </div>
          ) : (
            <DataTable
              columns={[...columns]}
              rows={[...items]}
              rowKey={(row) => row.reservationId}
              emptyState={t('cms.popups.reservationsEmpty')}
              stickyHeader
              /* QA-184: 컬럼 헤더 클릭 정렬(오름/내림 토글). 서버 정렬로 전달하므로 정렬 변경 시
                 1페이지로 리셋해 전체 결과의 첫 페이지를 보여준다. */
              sort={sort}
              onSortChange={(key) => {
                setSort((prev) => toggleDataTableSort(prev, key));
                setPage(1);
              }}
              /* QA-200: 현장 QR용 모바일(<991px) — 와이드 예약 테이블을 행→카드로 접어
                 좁은 폭에서도 예약번호/예약자/회차/상태를 읽을 수 있게 한다. */
              mobileCardCollapse
            />
          )}

          {pagination ? (
            <Pagination
              total={pagination.totalItems}
              currentPage={pagination.page}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
              className="mt-4"
            />
          ) : null}
        </SectionCard>
      )}
    </PageShell>
  );
};

// ---------------------------------------------------------------------------
// QA-184: 예약 현황 캘린더 — 슬롯별 정원/잔여석을 월 그리드로. 기존 슬롯 조회 재사용.
// ---------------------------------------------------------------------------

const ymKey = (d: Date): string => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const ymLabel = (d: Date): string => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}`;

/** 로컬 Date → 'YYYY-MM-DD'(슬롯 slotDate 키 정합). */
const isoDate = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** 해당 월을 일~토 6주 그리드로(앞뒤 달 일부 포함). */
const buildMonthGrid = (cursor: Date): Date[][] => {
  const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay()); // 그 주 일요일로 back
  const weeks: Date[][] = [];
  const d = new Date(start);
  for (let w = 0; w < 6; w += 1) {
    const week: Date[] = [];
    for (let i = 0; i < 7; i += 1) {
      week.push(new Date(d));
      d.setDate(d.getDate() + 1);
    }
    weeks.push(week);
  }
  return weeks;
};

interface ReservationCalendarProps {
  popupId: string;
}

const ReservationCalendar = ({ popupId }: ReservationCalendarProps) => {
  const { t } = useTranslation('common');
  // 슬롯 전체를 받아 월별로 묶는다(슬롯 조회는 날짜 미지정 시 전체 반환).
  const slotsQuery = usePopupSlotsQuery(popupId);
  const [cursor, setCursor] = useState(() => new Date());

  const slotsByDate = useMemo(() => {
    const map = new Map<string, PopupSlotView[]>();
    for (const s of slotsQuery.data ?? []) {
      const list = map.get(s.slotDate) ?? [];
      list.push(s);
      map.set(s.slotDate, list);
    }
    // 같은 날짜 내 시작시간 순 정렬.
    for (const list of map.values()) {
      list.sort((a, b) => a.timeStart.localeCompare(b.timeStart));
    }
    return map;
  }, [slotsQuery.data]);

  const weeks = useMemo(() => buildMonthGrid(cursor), [cursor]);
  const monthCur = ymKey(cursor);
  const weekdayLabels = ['일', '월', '화', '수', '목', '금', '토'];

  if (slotsQuery.isPending) {
    return (
      <SectionCard title={t('cms.popups.reservationCalendar.title', { defaultValue: '예약 현황 캘린더' })}>
        <div role="status" aria-live="polite" className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-[64px] w-full" />
          ))}
        </div>
      </SectionCard>
    );
  }

  return (
    <SectionCard title={t('cms.popups.reservationCalendar.title', { defaultValue: '예약 현황 캘린더' })}>
      <div className="mb-3 flex items-center justify-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('cms.popups.reservationCalendar.prevMonth', { defaultValue: '이전 달' })}
          onClick={() => setCursor((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1))}
        >
          ‹
        </Button>
        <span className="text-sm font-semibold tabular-nums" style={{ color: 'var(--content-strong)' }}>
          {ymLabel(cursor)}
        </span>
        <Button
          variant="ghost"
          size="sm"
          aria-label={t('cms.popups.reservationCalendar.nextMonth', { defaultValue: '다음 달' })}
          onClick={() => setCursor((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1))}
        >
          ›
        </Button>
      </div>

      {/* QA-200: 모바일(<md)에서 7열 그리드가 셀당 ~50px 로 짓눌려 시간/정원 텍스트가 깨지므로
          가로 스크롤 컨테이너로 감싸고 그리드에 최소폭(7×84px)을 줘 셀 가독성을 보존한다.
          데스크톱은 컨테이너 폭에 맞춰 그대로 채워진다(min-w 이하로 줄지 않음). */}
      <div className="overflow-x-auto">
      <div
        className="grid grid-cols-7 overflow-hidden rounded-lg text-center"
        style={{ border: '1px solid var(--divider-strong)', minWidth: 588 }}
        role="grid"
        aria-label={t('cms.popups.reservationCalendar.title', { defaultValue: '예약 현황 캘린더' })}
      >
        {weekdayLabels.map((w) => (
          <div
            key={w}
            className="border-b py-2 text-xs font-medium"
            style={{ background: 'var(--surface-1)', borderColor: 'var(--divider-strong)', color: 'var(--content-muted)' }}
            role="columnheader"
          >
            {w}
          </div>
        ))}
        {weeks.flat().map((day) => {
          const inMonth = ymKey(day) === monthCur;
          const key = isoDate(day);
          const slots = slotsByDate.get(key) ?? [];
          return (
            <div
              key={key}
              role="gridcell"
              className="min-h-[88px] border-b border-r p-1.5 text-left align-top"
              style={{
                borderColor: 'var(--divider-strong)',
                background: inMonth ? 'var(--surface-0)' : 'var(--surface-1)',
                opacity: inMonth ? 1 : 0.5,
              }}
            >
              <div className="mb-1 text-[11px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
                {day.getDate()}
              </div>
              <div className="flex flex-col gap-0.5">
                {slots.map((s) => {
                  const cur = s.currentReservations ?? 0;
                  const max = s.maxParticipants;
                  // QA-R6 N: 정원 null/0(이하) = 무제한 → 만석 아님, 분모(/N) 미표기.
                  const hasCap = max != null && max > 0;
                  const full = hasCap && cur >= max;
                  return (
                    <div
                      key={s.id}
                      className="rounded px-1 py-0.5 text-[10px] tabular-nums"
                      style={{
                        background: full ? 'var(--status-danger-bg)' : 'var(--surface-1)',
                        color: full ? 'var(--status-danger-fg)' : 'var(--content-default)',
                      }}
                      title={`${s.timeStart.slice(0, 5)} ${cur}${hasCap ? `/${max}` : ''}`}
                    >
                      {s.timeStart.slice(0, 5)} {cur}{hasCap ? `/${max}` : ''}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      </div>

      <p className="mt-2 text-[11px]" style={{ color: 'var(--content-muted)' }}>
        {t('cms.popups.reservationCalendar.legend', {
          defaultValue: '셀의 시간 옆 숫자 = 예약/정원. 만석은 강조 표시됩니다.',
        })}
      </p>
    </SectionCard>
  );
};

interface QrVerifyPanelProps {
  popupId: string;
}

const QrVerifyPanel = ({ popupId }: QrVerifyPanelProps) => {
  const { t } = useTranslation('common');
  const verify = usePopupQrVerifyMutation(popupId);
  const [token, setToken] = useState('');

  const submit = () => {
    const value = token.trim();
    if (!value) return;
    verify.mutate(value, { onSuccess: () => setToken('') });
  };

  const errorMessage = (() => {
    if (!verify.isError) return null;
    const err = verify.error;
    if (err instanceof QrVerifyError) {
      if (err.status === 404) return t('cms.popups.qr.notFound');
      if (err.status === 409) return t('cms.popups.qr.alreadyProcessed');
    }
    return t('cms.popups.qr.failed');
  })();

  const verified = verify.data;

  // B-1/B-3: 스캔 즉시 입장 처리(qr-verify). onSuccess 가 예약목록 invalidate → 상태 입장완료 반영.
  const handleScan = (scanned: string) => {
    const value = scanned.trim();
    if (!value || verify.isPending) return;
    verify.mutate(value);
  };

  return (
    <SectionCard
      title={t('cms.popups.qr.title')}
      description={t('cms.popups.qr.manualHint')}
    >
      <div className="flex flex-col gap-3">
        <QrCameraScanner onScan={handleScan} disabled={verify.isPending} />

        <div className="flex items-center gap-2">
          <span className="text-xs font-medium" style={{ color: 'var(--content-muted)' }}>
            또는 토큰 직접 입력
          </span>
          <div className="h-px flex-1" style={{ background: 'var(--divider-strong)' }} />
        </div>

        <div className="flex items-center gap-2">
          <Input
            value={token}
            onChange={(e) => setToken(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submit();
              }
            }}
            placeholder={t('cms.popups.qr.tokenPlaceholder')}
            className="font-mono"
            aria-label={t('cms.popups.qr.tokenLabel')}
          />
          <Button disabled={verify.isPending || token.trim() === ''} onClick={submit}>
            <ScanLine size={15} aria-hidden="true" />
            {verify.isPending ? t('cms.popups.qr.verifying') : t('cms.popups.qr.verify')}
          </Button>
        </div>

        {errorMessage ? (
          <p role="alert" className="text-sm" style={{ color: 'var(--status-danger-fg)' }}>
            {errorMessage}
          </p>
        ) : null}

        {verified ? (
          <div
            className="rounded-md p-3 text-sm"
            style={{ background: 'var(--status-success-bg)', color: 'var(--status-success-fg)' }}
            aria-live="polite"
          >
            <p className="font-semibold">{t('cms.popups.qr.entered')}</p>
            <p className="mt-1" style={{ color: 'var(--content-strong)' }}>
              {verified.reserverName ?? '-'} · {verified.slotDate} {verified.slotTime} ·{' '}
              {t('cms.popups.qr.people', { count: verified.peopleCount })}
            </p>
          </div>
        ) : null}
      </div>
    </SectionCard>
  );
};
